import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Loader2, FileOutput, Check } from "lucide-react";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;

function ExtractPdfPages() {
  const [file, setFile] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [pageImages, setPageImages] = useState([]);
  const [selectedPages, setSelectedPages] = useState(new Set());
  const [pageRange, setPageRange] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Extract PDF Pages - Get Specific Pages from PDF - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setSelectedPages(new Set());
      setPageRange("");
      await loadPdf(selectedFile);
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setSelectedPages(new Set());
      setPageRange("");
      await loadPdf(droppedFile);
    } else {
      toast.error("Please drop a valid PDF file");
    }
  };

  const loadPdf = async (pdfFile) => {
    setIsProcessing(true);
    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      setPdfBytes(new Uint8Array(arrayBuffer));
      
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const images = [];
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 0.5 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        await page.render({ canvasContext: context, viewport }).promise;
        images.push({
          pageNum: i,
          dataUrl: canvas.toDataURL()
        });
      }
      
      setPageImages(images);
      toast.success(`PDF loaded: ${pdf.numPages} pages. Select pages to extract.`);
    } catch (error) {
      console.error("Error loading PDF:", error);
      toast.error("Failed to load PDF");
    }
    setIsProcessing(false);
  };

  const togglePage = (pageNum) => {
    const newSelected = new Set(selectedPages);
    if (newSelected.has(pageNum)) {
      newSelected.delete(pageNum);
    } else {
      newSelected.add(pageNum);
    }
    setSelectedPages(newSelected);
    updatePageRangeFromSelection(newSelected);
  };

  const updatePageRangeFromSelection = (selected) => {
    if (selected.size === 0) {
      setPageRange("");
      return;
    }
    
    const sortedPages = Array.from(selected).sort((a, b) => a - b);
    const ranges = [];
    let start = sortedPages[0];
    let end = sortedPages[0];
    
    for (let i = 1; i < sortedPages.length; i++) {
      if (sortedPages[i] === end + 1) {
        end = sortedPages[i];
      } else {
        ranges.push(start === end ? `${start}` : `${start}-${end}`);
        start = sortedPages[i];
        end = sortedPages[i];
      }
    }
    ranges.push(start === end ? `${start}` : `${start}-${end}`);
    
    setPageRange(ranges.join(", "));
  };

  const parsePageRange = (rangeStr) => {
    const pages = new Set();
    const parts = rangeStr.split(",").map(p => p.trim()).filter(p => p);
    
    for (const part of parts) {
      if (part.includes("-")) {
        const [start, end] = part.split("-").map(n => parseInt(n.trim()));
        if (!isNaN(start) && !isNaN(end)) {
          for (let i = Math.min(start, end); i <= Math.max(start, end); i++) {
            if (i >= 1 && i <= pageImages.length) {
              pages.add(i);
            }
          }
        }
      } else {
        const num = parseInt(part);
        if (!isNaN(num) && num >= 1 && num <= pageImages.length) {
          pages.add(num);
        }
      }
    }
    
    return pages;
  };

  const handlePageRangeChange = (value) => {
    setPageRange(value);
    const parsed = parsePageRange(value);
    setSelectedPages(parsed);
  };

  const selectAll = () => {
    if (selectedPages.size === pageImages.length) {
      setSelectedPages(new Set());
      setPageRange("");
    } else {
      const allPages = new Set(pageImages.map(p => p.pageNum));
      setSelectedPages(allPages);
      setPageRange(`1-${pageImages.length}`);
    }
  };

  const extractPages = async () => {
    if (selectedPages.size === 0) {
      toast.error("Please select pages to extract");
      return;
    }

    setIsProcessing(true);
    try {
      const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
      const newPdfDoc = await PDFDocument.create();
      
      // Get pages to extract (sorted)
      const pagesToExtract = Array.from(selectedPages).sort((a, b) => a - b).map(p => p - 1);
      
      // Copy selected pages
      const copiedPages = await newPdfDoc.copyPages(pdfDoc, pagesToExtract);
      copiedPages.forEach(page => newPdfDoc.addPage(page));
      
      const newPdfBytes = await newPdfDoc.save();
      const blob = new Blob([newPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Generate filename with page range
      const sortedPages = Array.from(selectedPages).sort((a, b) => a - b);
      const rangeStr = sortedPages.length <= 3 
        ? sortedPages.join("_") 
        : `${sortedPages[0]}-${sortedPages[sortedPages.length - 1]}`;
      a.download = file.name.replace('.pdf', `_pages_${rangeStr}.pdf`);
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Extracted ${selectedPages.size} page${selectedPages.size !== 1 ? 's' : ''}!`);
    } catch (error) {
      console.error("Error extracting pages:", error);
      toast.error("Failed to extract pages: " + error.message);
    }
    setIsProcessing(false);
  };

  const clearFile = () => {
    setFile(null);
    setPdfBytes(null);
    setPageImages([]);
    setSelectedPages(new Set());
    setPageRange("");
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-indigo-50 to-blue-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
              <FileOutput className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-blue-600 text-white rounded-full mb-4">
            <FileOutput className="w-5 h-5" />
            <span className="font-medium">Extract PDF Pages</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Extract Pages from PDF
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Select specific pages or page ranges to extract from your PDF document.
          </p>
        </div>

        {/* Upload Area */}
        {!file && (
          <Card 
            className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 max-w-2xl mx-auto ${
              isDragging ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-slate-300 dark:border-slate-600'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <CardContent className="p-12 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <div 
                className="cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-zone"
              >
                <Upload className="w-16 h-16 mx-auto mb-4 text-indigo-500" />
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  Drop PDF or click to upload
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Select a PDF to extract pages from
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {file && (
          <div className="max-w-6xl mx-auto space-y-6">
            {/* Controls */}
            <Card className="dark:bg-slate-800">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <FileText className="w-8 h-8 text-indigo-500" />
                    <div>
                      <h3 className="font-semibold dark:text-white">{file.name}</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {pageImages.length} pages • {selectedPages.size} selected
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={selectAll}>
                      {selectedPages.size === pageImages.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={clearFile}>
                      <X className="w-4 h-4 mr-1" /> Clear
                    </Button>
                  </div>
                </div>
                
                {/* Page Range Input */}
                <div className="mt-4 flex flex-wrap items-end gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-sm mb-1 block">Page Range (e.g., 1-3, 5, 7-10)</Label>
                    <Input
                      value={pageRange}
                      onChange={(e) => handlePageRangeChange(e.target.value)}
                      placeholder="Enter page numbers..."
                      className="max-w-md"
                    />
                  </div>
                  <Button 
                    onClick={extractPages}
                    disabled={selectedPages.size === 0 || isProcessing}
                    className="bg-gradient-to-r from-indigo-500 to-blue-600"
                  >
                    {isProcessing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Extract {selectedPages.size} Page{selectedPages.size !== 1 ? 's' : ''}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Page Grid */}
            {isProcessing && pageImages.length === 0 ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {pageImages.map((page) => {
                  const isSelected = selectedPages.has(page.pageNum);
                  return (
                    <Card 
                      key={page.pageNum} 
                      className={`cursor-pointer overflow-hidden transition-all hover:shadow-lg ${
                        isSelected 
                          ? 'ring-2 ring-indigo-500 dark:ring-indigo-400 bg-indigo-50 dark:bg-indigo-900/20' 
                          : 'dark:bg-slate-800'
                      }`}
                      onClick={() => togglePage(page.pageNum)}
                    >
                      <div className="relative">
                        <img
                          src={page.dataUrl}
                          alt={`Page ${page.pageNum}`}
                          className={`w-full h-auto transition-opacity ${isSelected ? 'opacity-90' : ''}`}
                        />
                        <div className="absolute top-2 left-2">
                          <Badge className={isSelected ? 'bg-indigo-500' : 'bg-black/70'}>
                            {page.pageNum}
                          </Badge>
                        </div>
                        <div className="absolute top-2 right-2">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                            isSelected 
                              ? 'bg-indigo-500 border-indigo-500 text-white' 
                              : 'bg-white/80 border-slate-300'
                          }`}>
                            {isSelected && <Check className="w-4 h-4" />}
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Extract Button (Large) */}
            {selectedPages.size > 0 && (
              <div className="text-center">
                <Button 
                  onClick={extractPages}
                  disabled={isProcessing}
                  size="lg"
                  className="bg-gradient-to-r from-indigo-500 to-blue-600"
                >
                  {isProcessing ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5 mr-2" />
                  )}
                  Extract {selectedPages.size} Page{selectedPages.size !== 1 ? 's' : ''} as New PDF
                </Button>
              </div>
            )}
          </div>
        )}

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default ExtractPdfPages;
